/**
 * Billiards AI Player
 *
 * Geometry-based AI with 3 difficulty levels:
 * - Easy: Random valid shots with noise, often misses pockets
 * - Medium: Targets closest ball to pocket, moderate accuracy
 * - Hard: Calculates optimal angles, considers position play, high accuracy
 */

import {
  BilliardsPlayer,
  BilliardsDifficulty,
  BilliardsBall,
  Shot,
  PlayerAssignment,
  SpinType,
  Vector2D,
  POCKET_POSITIONS,
  TABLE_WIDTH,
  TABLE_HEIGHT,
  CUSHION_WIDTH,
  BALL_RADIUS,
  CUE_BALL_RADIUS,
} from './types';
import { BilliardsEngine } from './BilliardsEngine';
import { BilliardsPhysics } from './BilliardsPhysics';

interface ShotCandidate {
  shot: Shot;
  score: number;
}

export class BilliardsAI {
  private difficulty: BilliardsDifficulty;
  private thinkingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: BilliardsDifficulty) {
    this.difficulty = difficulty;
  }

  /**
   * Calculate the best shot for the AI player.
   */
  calculateShot(engine: BilliardsEngine, player: BilliardsPlayer): Shot {
    switch (this.difficulty) {
      case 'easy':
        return this.calculateEasyShot(engine, player);
      case 'medium':
        return this.calculateMediumShot(engine, player);
      case 'hard':
        return this.calculateHardShot(engine, player);
      default:
        return this.calculateEasyShot(engine, player);
    }
  }

  /**
   * Schedule an AI shot with thinking delay.
   */
  scheduleShot(
    engine: BilliardsEngine,
    player: BilliardsPlayer,
    callback: (shot: Shot) => void
  ): void {
    const delay = this.getThinkingDelay();

    this.thinkingTimeout = setTimeout(() => {
      const shot = this.calculateShot(engine, player);
      callback(shot);
    }, delay);
  }

  destroy(): void {
    if (this.thinkingTimeout) {
      clearTimeout(this.thinkingTimeout);
      this.thinkingTimeout = null;
    }
  }

  // =========================================================================
  // Easy AI
  // =========================================================================

  private calculateEasyShot(engine: BilliardsEngine, _player: BilliardsPlayer): Shot {
    const cueBall = engine.getCueBall();
    if (!cueBall) return { angle: 0, power: 0.5, spin: 'none' };

    // Random angle with bias toward the cluster of balls
    const activeBalls = engine.getActiveBalls().filter((b) => b.type !== 'cue');
    if (activeBalls.length === 0) return { angle: 0, power: 0.5, spin: 'none' };

    // Pick a random ball and aim roughly at it with significant noise
    const targetBall = activeBalls[Math.floor(Math.random() * activeBalls.length)];
    const baseAngle = Math.atan2(targetBall.y - cueBall.y, targetBall.x - cueBall.x);

    // Add large noise (easy AI is inaccurate)
    const noise = (Math.random() - 0.5) * 0.6;
    const angle = baseAngle + noise;
    const power = 0.3 + Math.random() * 0.5;

    return { angle, power, spin: 'none' };
  }

  // =========================================================================
  // Medium AI
  // =========================================================================

  private calculateMediumShot(engine: BilliardsEngine, player: BilliardsPlayer): Shot {
    const cueBall = engine.getCueBall();
    if (!cueBall) return { angle: 0, power: 0.5, spin: 'none' };

    const assignment = engine.getPlayerAssignment(player);
    const activeBalls = engine.getActiveBalls().filter((b) => b.type !== 'cue');
    const targetBalls = this.getTargetBalls(activeBalls, assignment);

    if (targetBalls.length === 0) {
      return this.calculateEasyShot(engine, player);
    }

    // Find closest target ball to any pocket
    let bestBall: BilliardsBall | null = null;
    let bestPocket: Vector2D | null = null;
    let bestDist = Infinity;

    for (const ball of targetBalls) {
      for (const pocket of POCKET_POSITIONS) {
        const dist = BilliardsPhysics.distance(ball, pocket);
        if (dist < bestDist) {
          bestDist = dist;
          bestBall = ball;
          bestPocket = pocket;
        }
      }
    }

    if (!bestBall || !bestPocket) {
      return this.calculateEasyShot(engine, player);
    }

    const shot = this.aimAtBallToPocket(cueBall, bestBall, bestPocket);

    // Add moderate noise
    const noise = (Math.random() - 0.5) * 0.12;
    shot.angle += noise;
    shot.power = Math.max(0.25, Math.min(0.85, shot.power + (Math.random() - 0.5) * 0.15));

    return shot;
  }

  // =========================================================================
  // Hard AI
  // =========================================================================

  private calculateHardShot(engine: BilliardsEngine, player: BilliardsPlayer): Shot {
    const cueBall = engine.getCueBall();
    if (!cueBall) return { angle: 0, power: 0.5, spin: 'none' };

    const assignment = engine.getPlayerAssignment(player);
    const activeBalls = engine.getActiveBalls().filter((b) => b.type !== 'cue');
    const targetBalls = this.getTargetBalls(activeBalls, assignment);

    if (targetBalls.length === 0) {
      return this.calculateMediumShot(engine, player);
    }

    const candidates: ShotCandidate[] = [];

    // Evaluate all target balls x all pockets
    for (const ball of targetBalls) {
      for (const pocket of POCKET_POSITIONS) {
        const candidate = this.evaluateShot(cueBall, ball, pocket, activeBalls);
        if (candidate) {
          candidates.push(candidate);
        }
      }
    }

    if (candidates.length === 0) {
      return this.calculateMediumShot(engine, player);
    }

    // Sort by score (higher is better)
    candidates.sort((a, b) => b.score - a.score);

    // Pick the best shot with minimal noise
    const best = candidates[0];
    const noise = (Math.random() - 0.5) * 0.03;
    best.shot.angle += noise;

    // Hard AI may use spin for position play
    if (Math.random() > 0.6) {
      const spins: SpinType[] = ['top', 'back', 'left', 'right'];
      best.shot.spin = spins[Math.floor(Math.random() * spins.length)];
    }

    return best.shot;
  }

  private evaluateShot(
    cueBall: BilliardsBall,
    targetBall: BilliardsBall,
    pocket: Vector2D,
    allBalls: BilliardsBall[]
  ): ShotCandidate | null {
    // Calculate direction from ball to pocket
    const ballToPocketAngle = Math.atan2(pocket.y - targetBall.y, pocket.x - targetBall.x);

    // The cue ball needs to hit from the opposite side
    const hitX = targetBall.x - Math.cos(ballToPocketAngle) * (BALL_RADIUS + CUE_BALL_RADIUS);
    const hitY = targetBall.y - Math.sin(ballToPocketAngle) * (BALL_RADIUS + CUE_BALL_RADIUS);

    // Calculate angle from cue ball to hit point
    const angle = Math.atan2(hitY - cueBall.y, hitX - cueBall.x);

    // Check if hit point is on the table
    if (hitX < CUSHION_WIDTH || hitX > TABLE_WIDTH - CUSHION_WIDTH ||
        hitY < CUSHION_WIDTH || hitY > TABLE_HEIGHT - CUSHION_WIDTH) {
      return null;
    }

    // Calculate distances for power
    const distToHit = BilliardsPhysics.distance(cueBall, { x: hitX, y: hitY });
    const distBallToPocket = BilliardsPhysics.distance(targetBall, pocket);
    const totalDist = distToHit + distBallToPocket;
    const power = Math.min(0.85, Math.max(0.2, totalDist / 900));

    // Score the shot
    let score = 100;

    // Prefer shorter total distances
    score -= totalDist * 0.08;

    // Prefer good alignment
    const cueToBallAngle = Math.atan2(targetBall.y - cueBall.y, targetBall.x - cueBall.x);
    const angleDiff = Math.abs(this.normalizeAngle(cueToBallAngle - ballToPocketAngle));
    score -= angleDiff * 15;

    // Penalize if path is blocked
    const blocked = this.isPathBlocked(
      cueBall,
      { x: hitX, y: hitY, radius: CUE_BALL_RADIUS } as any,
      allBalls.filter((b) => b.id !== targetBall.id && b.id !== cueBall.id)
    );
    if (blocked) score -= 50;

    // Penalize if ball-to-pocket path is blocked
    const pocketBlocked = this.isPathBlocked(
      targetBall,
      { x: pocket.x, y: pocket.y, radius: BALL_RADIUS } as any,
      allBalls.filter((b) => b.id !== targetBall.id)
    );
    if (pocketBlocked) score -= 40;

    return {
      shot: { angle, power, spin: 'none' },
      score: Math.max(0, score),
    };
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private getTargetBalls(activeBalls: BilliardsBall[], assignment: PlayerAssignment): BilliardsBall[] {
    if (assignment === 'unassigned') {
      // When table is open, can target any non-8ball
      return activeBalls.filter((b) => b.type === 'solid' || b.type === 'stripe');
    }

    const targetType = assignment === 'solids' ? 'solid' : 'stripe';
    const ownBalls = activeBalls.filter((b) => b.type === targetType);

    // If all own balls are pocketed, target the 8-ball
    if (ownBalls.length === 0) {
      return activeBalls.filter((b) => b.type === '8ball');
    }

    return ownBalls;
  }

  private aimAtBallToPocket(
    cueBall: BilliardsBall,
    targetBall: BilliardsBall,
    pocket: Vector2D
  ): Shot {
    const ballToPocketAngle = Math.atan2(pocket.y - targetBall.y, pocket.x - targetBall.x);
    const hitX = targetBall.x - Math.cos(ballToPocketAngle) * (BALL_RADIUS + CUE_BALL_RADIUS);
    const hitY = targetBall.y - Math.sin(ballToPocketAngle) * (BALL_RADIUS + CUE_BALL_RADIUS);

    const angle = Math.atan2(hitY - cueBall.y, hitX - cueBall.x);
    const dist = BilliardsPhysics.distance(cueBall, { x: hitX, y: hitY });
    const power = Math.min(0.8, Math.max(0.3, dist / 600));

    return { angle, power, spin: 'none' };
  }

  private isPathBlocked(
    from: Vector2D,
    to: Vector2D,
    obstacles: BilliardsBall[]
  ): boolean {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len === 0) return false;

    const nx = dx / len;
    const ny = dy / len;

    for (const obs of obstacles) {
      if (obs.pocketed) continue;
      const ox = obs.x - from.x;
      const oy = obs.y - from.y;
      const proj = ox * nx + oy * ny;

      if (proj < 0 || proj > len) continue;

      const perpDist = Math.abs(ox * ny - oy * nx);
      if (perpDist < BALL_RADIUS + CUE_BALL_RADIUS) {
        return true;
      }
    }

    return false;
  }

  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= 2 * Math.PI;
    while (angle < -Math.PI) angle += 2 * Math.PI;
    return angle;
  }

  private getThinkingDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return 600 + Math.random() * 600;
      case 'medium':
        return 900 + Math.random() * 700;
      case 'hard':
        return 1200 + Math.random() * 1000;
      default:
        return 600;
    }
  }
}
